import type { FirebaseApp } from 'firebase/app';
import { doc, getDoc, serverTimestamp, setDoc, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';

import type { EmulatorEndpoint } from '@taki/runtime-config';
import { COULEURS_DE_FICHE_PERSO_DEFAUT, HAUTEUR_DE_FICHE_PERSO_DEFAUT, VUE_HTML_BROUILLONS, type CouleursDeFichePerso, type HauteurDeFichePerso, type VueHtmlBrouillon } from '@taki/data-model';

/**
 * LES VUES HTML D'UNE FICHE, COTE ECRAN (`docs/socle/vues-html.md`).
 *
 * Le MCP les depose (`vue_proposer` ecrit un brouillon d'une heure, `vue_publier`
 * le publie). L'ecran en fait deux choses, et rien d'autre :
 *
 * - `lireBrouillon(id)` : l'APERCU (`?apercu=<id>` dans l'adresse) lit le
 *   brouillon, que les rules ne laissent lire qu'a son auteur, tant qu'il n'a
 *   pas expire. Absent, expire, d'un autre : `null`, jamais une erreur a
 *   afficher telle quelle ;
 * - `publier(id)` : le bouton « Publier » de l'apercu. La callable
 *   `publierVueHtml` refait TOUS les controles de `vue_publier` (auteur, non
 *   expire, administrateur ou proprietaire, champs et liens) et ecrit par le
 *   meme chemin que le Studio (`functions/src/ia/vues-html.ts`).
 *
 * - `deposer(entree)` : le bouton « Publier » de l'ATELIER de la fiche
 *   personnalisee (une page deposee, sans brouillon visible). La callable
 *   `deposerVueHtml` fait `vue_proposer` puis `vue_publier` du meme appelant,
 *   memes controles, meme chemin de publication.
 *
 * - `couleurs.lire` / `couleurs.ecrire` et `hauteur.lire` / `hauteur.ecrire` : les
 *   DEUX CHOIX de la fiche personnalisee d'une porte (« Couleurs de
 *   l'environnement » ou « Mes propres couleurs » ; « Défilement » ou
 *   « Fixe »), un document par personne, `orgs/{orgId}/preferencesVues/{uid}`
 *   (rules : son uid seul le lit et l'ecrit), `{ orgId, uid, couleurs: {
 *   '<appId>:<porteId>': 'environnement' | 'propres' }, hauteur: {
 *   '<appId>:<porteId>': 'defilement' | 'fixe' }, updatedAt }`. Une SEULE
 *   source, lisible du serveur : `vue_contrat` la lit pour servir a l'agent la
 *   meme consigne que l'atelier.
 *
 * Le client n'ecrit jamais un brouillon ni une declaration lui-meme.
 */

/** DOIT valoir `FUNCTIONS_REGION` de `functions/src/runtime.ts`. */
export const VUES_HTML_FUNCTIONS_REGION = 'europe-west1';
/** `CALLABLE_PUBLIER_VUE_HTML` de `functions/src/ia/vues-html.ts`. */
export const CALLABLE_PUBLIER_VUE_HTML = 'publierVueHtml';
/** `CALLABLE_DEPOSER_VUE_HTML` de `functions/src/ia/vues-html.ts`. */
export const CALLABLE_DEPOSER_VUE_HTML = 'deposerVueHtml';

/** Ce que rend une publication : la vue, sa version, son app. */
export interface VueHtmlPubliee {
  appId: string;
  viewId: string;
  version: number;
}

/** Ce que l'atelier depose : les arguments de `vue_proposer`, publies d'un geste. */
export interface EntreeDeDepotDeVueHtml {
  appId: string;
  door: string;
  label: string;
  fields: string[];
  links?: string[];
  html: string;
  recordId?: string;
  viewId?: string;
}

/** `PREFERENCES_VUES` de `functions/src/ia/vues-html.ts`. */
export const PREFERENCES_VUES_COLLECTION = 'preferencesVues';
/** Le plafond d'entrees de `couleurs`, recopie dans les rules. */
export const COULEURS_PREFERENCES_MAX = 200;
/** Le plafond d'entrees de `hauteur`, recopie dans les rules. */
export const HAUTEUR_PREFERENCES_MAX = 200;

/** La clef d'une porte dans `couleurs`. */
export function cleDeCouleurs(appId: string, porteId: string): string {
  return `${appId}:${porteId}`;
}

/** Pure : le choix d'un document de preferences pour une porte ; absent ou mal forme, l'environnement. */
export function choixDeCouleurs(brut: unknown, appId: string, porteId: string): CouleursDeFichePerso {
  const couleurs = brut != null && typeof brut === 'object' ? (brut as { couleurs?: unknown }).couleurs : undefined;
  const valeur = couleurs != null && typeof couleurs === 'object' ? (couleurs as Record<string, unknown>)[cleDeCouleurs(appId, porteId)] : undefined;
  return valeur === 'propres' ? 'propres' : COULEURS_DE_FICHE_PERSO_DEFAUT;
}

/** Pure : la hauteur d'un document de preferences pour une porte ; absente ou mal formee, le defilement. */
export function choixDeHauteur(brut: unknown, appId: string, porteId: string): HauteurDeFichePerso {
  const hauteur = brut != null && typeof brut === 'object' ? (brut as { hauteur?: unknown }).hauteur : undefined;
  const valeur = hauteur != null && typeof hauteur === 'object' ? (hauteur as Record<string, unknown>)[cleDeCouleurs(appId, porteId)] : undefined;
  return valeur === 'fixe' ? 'fixe' : HAUTEUR_DE_FICHE_PERSO_DEFAUT;
}

/** Le choix des couleurs d'une fiche personnalisee, par app et par porte. */
export interface CouleursDeVuesClient {
  /** Le choix de la personne ; absent, illisible ou refuse par les rules : l'environnement. */
  lire(appId: string, porteId: string): Promise<CouleursDeFichePerso>;
  /** Enregistre le choix (la clef seule est fusionnee, les autres portes restent). */
  ecrire(appId: string, porteId: string, choix: CouleursDeFichePerso): Promise<void>;
}

/** Le choix de la hauteur d'une fiche personnalisee, par app et par porte. */
export interface HauteurDeVuesClient {
  /** Le choix de la personne ; absent, illisible ou refuse par les rules : le defilement. */
  lire(appId: string, porteId: string): Promise<HauteurDeFichePerso>;
  /** Enregistre le choix (la clef seule est fusionnee, les autres portes restent). */
  ecrire(appId: string, porteId: string, choix: HauteurDeFichePerso): Promise<void>;
}

export interface VuesHtmlClient {
  couleurs: CouleursDeVuesClient;
  hauteur: HauteurDeVuesClient;
  lireBrouillon(id: string): Promise<VueHtmlBrouillon | null>;
  publier(id: string): Promise<VueHtmlPubliee>;
  deposer(entree: EntreeDeDepotDeVueHtml): Promise<VueHtmlPubliee>;
}

const ID_BROUILLON = /^[A-Za-z0-9_-]{1,128}$/;
const emulatorBoundFunctions = new WeakSet<Functions>();

function date(valeur: unknown): Date | null {
  return valeur != null && typeof valeur === 'object' && 'toDate' in valeur && typeof valeur.toDate === 'function' ? valeur.toDate() as Date : null;
}
function chaines(valeur: unknown): string[] {
  return Array.isArray(valeur) ? valeur.filter((item): item is string => typeof item === 'string') : [];
}

/** Un document de brouillon lu, ramene au contrat ; `null` s'il n'en a pas la forme. */
export function lireDocumentBrouillon(id: string, donnees: Record<string, unknown> | undefined): VueHtmlBrouillon | null {
  if (!donnees) return null;
  const jeu = donnees['jeu'] as { family?: unknown; type?: unknown } | undefined;
  const texte = (clef: string) => (typeof donnees[clef] === 'string' ? donnees[clef] as string : null);
  const appId = texte('appId'), porte = texte('porte'), viewId = texte('viewId'), label = texte('label'), html = texte('html'), uid = texte('uid');
  if (!appId || !porte || !viewId || !label || html === null || !uid || typeof jeu?.family !== 'string') return null;
  const liens = chaines(donnees['liens']);
  return {
    id, appId, porte, viewId, label, html, uid,
    jeu: { family: jeu.family, ...(typeof jeu.type === 'string' ? { type: jeu.type } : {}) },
    champs: chaines(donnees['champs']),
    ...(Array.isArray(donnees['liens']) ? { liens } : {}),
    recordId: texte('recordId'),
    expiresAt: date(donnees['expiresAt']),
  };
}

function codeDe(erreur: unknown): string {
  return erreur != null && typeof erreur === 'object' && typeof (erreur as { code?: unknown }).code === 'string' ? (erreur as { code: string }).code : '';
}

export function createVuesHtmlClient(db: Firestore, orgId: string, app: FirebaseApp, emulator?: EmulatorEndpoint | undefined, getActorId: () => string = () => ''): VuesHtmlClient {
  let appeler: ReturnType<typeof httpsCallable<{ brouillonId: string }, VueHtmlPubliee>> | null = null;
  let deposeur: ReturnType<typeof httpsCallable<EntreeDeDepotDeVueHtml, VueHtmlPubliee>> | null = null;
  /* Les fonctions de la region, branchees UNE fois sur l'emulateur : les deux callables partagent ce cablage. */
  const fonctions = (): Functions => {
    const functions = getFunctions(app, VUES_HTML_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    return functions;
  };
  const preferences = () => doc(db, 'orgs', orgId, PREFERENCES_VUES_COLLECTION, getActorId());
  return {
    couleurs: {
      async lire(appId, porteId) {
        if (getActorId() === '') return COULEURS_DE_FICHE_PERSO_DEFAUT;
        try {
          const snap = await getDoc(preferences());
          return choixDeCouleurs(snap.exists() ? snap.data() : null, appId, porteId);
        } catch {
          // Illisible (rules, reseau) : une aide d'ecriture ne bloque jamais l'atelier.
          return COULEURS_DE_FICHE_PERSO_DEFAUT;
        }
      },
      async ecrire(appId, porteId, choix) {
        if (choix !== 'environnement' && choix !== 'propres') throw new Error(`vuesHtml.couleurs.ecrire : choix inconnu (${String(choix)}).`);
        await setDoc(preferences(), { orgId, uid: getActorId(), couleurs: { [cleDeCouleurs(appId, porteId)]: choix }, updatedAt: serverTimestamp() }, { merge: true });
      },
    },
    hauteur: {
      async lire(appId, porteId) {
        if (getActorId() === '') return HAUTEUR_DE_FICHE_PERSO_DEFAUT;
        try {
          const snap = await getDoc(preferences());
          return choixDeHauteur(snap.exists() ? snap.data() : null, appId, porteId);
        } catch {
          // Illisible (rules, reseau) : une aide d'ecriture ne bloque jamais l'atelier.
          return HAUTEUR_DE_FICHE_PERSO_DEFAUT;
        }
      },
      async ecrire(appId, porteId, choix) {
        if (choix !== 'defilement' && choix !== 'fixe') throw new Error(`vuesHtml.hauteur.ecrire : choix inconnu (${String(choix)}).`);
        await setDoc(preferences(), { orgId, uid: getActorId(), hauteur: { [cleDeCouleurs(appId, porteId)]: choix }, updatedAt: serverTimestamp() }, { merge: true });
      },
    },
    async lireBrouillon(id) {
      if (!ID_BROUILLON.test(id)) return null;
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId, VUE_HTML_BROUILLONS, id));
        const brouillon = lireDocumentBrouillon(snap.id, snap.data());
        if (brouillon?.expiresAt && brouillon.expiresAt.getTime() <= Date.now()) return null;
        return brouillon;
      } catch (erreur) {
        // Absent, expire ou d'un autre auteur : les rules refusent la lecture.
        if (codeDe(erreur) === 'permission-denied' || codeDe(erreur) === 'not-found') return null;
        throw erreur;
      }
    },
    async publier(id) {
      if (!ID_BROUILLON.test(id)) throw new Error('Brouillon de vue invalide.');
      if (appeler === null) appeler = httpsCallable<{ brouillonId: string }, VueHtmlPubliee>(fonctions(), CALLABLE_PUBLIER_VUE_HTML);
      return (await appeler({ brouillonId: id })).data;
    },
    async deposer(entree) {
      if (deposeur === null) deposeur = httpsCallable<EntreeDeDepotDeVueHtml, VueHtmlPubliee>(fonctions(), CALLABLE_DEPOSER_VUE_HTML);
      return (await deposeur(entree)).data;
    },
  };
}
